"use client";

import {
  shopCatalogApi,
  shopInventoryApi,
  type ShopBranchOption,
  type ShopInventoryStockRow,
  type ShopSellableCatalogRow,
} from "@/modules/merchant/presentation/api/client";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { Button, Label, Loader, Modal, toast } from "@devhop/ui";
import { useState } from "react";

type ShopInventoryAdjustPickModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  onPick: (row: ShopInventoryStockRow) => void;
};

export function ShopInventoryAdjustPickModal({
  open,
  onOpenChange,
  shopId,
  onPick,
}: ShopInventoryAdjustPickModalProps) {
  const [branchId, setBranchId] = useState("");
  const [skuId, setSkuId] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setBranchId("");
    setSkuId("");
    setLoading(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleContinue = async () => {
    const bid = branchId.trim();
    const sid = skuId.trim();
    if (!bid) {
      toast.error("ເລືອກສາຂາ");
      return;
    }
    if (!sid) {
      toast.error("ເລືອກສິນຄ້າ / SKU");
      return;
    }

    setLoading(true);
    try {
      const res = await shopInventoryApi.listStock(shopId, {
        branchId: bid,
        shopProductSkuId: sid,
        limit: 1,
        offset: 0,
      });
      const row = res.data[0];
      if (!row) {
        toast.error("ບໍ່ພົບແຖວສະຕ໋ອກສຳລັບສາຂາ ແລະ SKU ນີ້");
        return;
      }
      const tm =
        row.trackingMode === "serial" ? "lot_serial" : row.trackingMode;
      if (tm === "lot" || tm === "lot_serial") {
        toast.error(
          "SKU ນີ້ຕິດຕາມ lot/serial — ໃຊ້ຮັບເຂົ້າແທນການປັບສະຕ໋ອກ",
        );
        return;
      }
      onPick(row);
      handleOpenChange(false);
    } catch {
      toast.error("ໂຫຼດຂໍ້ມູນສະຕ໋ອກບໍ່ສຳເລັດ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={handleOpenChange}
      title="ເລືອກສິນຄ້າປັບສຕ໋ອກ"
      size="md"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>ສາຂາ</Label>
          <InfiniteCombobox<ShopBranchOption>
            queryKey={["shops", shopId, "inventory-adjust-pick", "branch"]}
            queryFn={async ({ search: q, pageParam }) => {
              return shopInventoryApi.lookupBranches(shopId, {
                page: pageParam,
                search: q.trim() || undefined,
                limit: 20,
              });
            }}
            preloadQueryFn={(id) => shopInventoryApi.getBranch(shopId, id)}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={branchId}
            onValueChange={setBranchId}
            placeholder="ເລືອກສາຂາ..."
            className="h-9 w-full"
            clearable
          />
        </div>

        <div className="space-y-2">
          <Label>ສິນຄ້າ / SKU</Label>
          <InfiniteCombobox<ShopSellableCatalogRow>
            queryKey={["shops", shopId, "inventory-adjust-pick", "sku"]}
            queryFn={async ({ search: q, pageParam }) => {
              const limit = 20;
              const page = pageParam ?? 1;
              const offset = (page - 1) * limit;
              const res = await shopCatalogApi.listSellable(shopId, {
                offset,
                limit,
                q: q.trim() || undefined,
                barcode: undefined,
              });
              const next =
                offset + res.data.length < res.meta.total ? page + 1 : null;
              return { items: res.data, nextPage: next };
            }}
            preloadQueryFn={(id) => shopCatalogApi.getSellableSku(shopId, id)}
            getLabel={(item) => `${item.productName} · ${item.shopSku}`}
            getValue={(item) => item.id}
            value={skuId}
            onValueChange={setSkuId}
            placeholder="ຄົ້ນຫາ: ຊື່, barcode, OEM..."
            className="h-9 w-full"
            clearable
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={loading}
          >
            ຍົກເລີກ
          </Button>
          <Button type="button" onClick={() => void handleContinue()} disabled={loading}>
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader />
                ກຳລັງໂຫຼດ
              </span>
            ) : (
              "ຕໍ່ໄປ"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
